import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, Button, FormControl, FormLabel, IconButton, InputAdornment, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useFinanceOutMutation } from 'src/redux/api/cabinet';
import { updProfileData } from 'src/redux/slices/app';
import { AppState } from 'src/store/Store';
import { palette } from 'src/theme/style/palette';
import { Alerting } from 'src/views/components_cabinet/Alerting';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const FormWithdrawBalance = () => {
    const user = useSelector((state: AppState) => state.app);

    const [showPassword, setShowPassword] = React.useState(false);
    const [amount, setAmount] = React.useState<string>('');
    const [comment, setComment] = React.useState<string>('');
    const [selectedWallet, setSelectedWallet] = useState<string>('');

    const [FP, setFP] = React.useState<string>('');
    const [getAlert, setgetAlert] = useState<{ type: number, msg: string } | undefined>(undefined);
    const dispatch = useDispatch();

    // Доступные методы выплаты. value берётся из данных пользователя.
    const walletMethods = [
        { key: 'wallet', label: 'USDT BEP-20', value: user?.data?.wallet },
        { key: 'wallet_btc', label: 'BTC', value: user?.data?.wallet_btc },
    ];

    const availableMethods = walletMethods.filter((m) => m.value);
    const hasNoMethods = availableMethods.length === 0;

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    // Если доступен хотя бы один метод — выбираем первый доступный по умолчанию.
    useEffect(() => {
        if (availableMethods.length > 0) {
            const stillValid = availableMethods.some((m) => m.key === selectedWallet);
            if (!stillValid) {
                setSelectedWallet(availableMethods[0].key);
            }
        } else {
            setSelectedWallet('');
        }
    }, [user?.data?.wallet, user?.data?.wallet_btc]);

    const SubmitForm = () => {
        const numericAmount = parseFloat(amount);

        if (hasNoMethods) {
            setgetAlert({ type: 2, msg: 'Нет доступных методов выплаты. Зайдите в настройки и настройте кошелёк.' });
            return false
        }
        if (!selectedWallet) {
            setgetAlert({ type: 2, msg: 'Выберите метод выплаты' });
            return false
        }
        if (!amount || numericAmount === 0) {
            setgetAlert({ type: 2, msg: 'Укажите сумму вывода' });
            return false
        }
        if (FP.length < 8) {
            setgetAlert({ type: 2, msg: 'Укажите Финансовый пароль (от 8 символов)' });
            return false
        }

        const data = { finance_password: FP, amount: numericAmount, comment, wallet: selectedWallet };
        getRequest(data);
    }

    const [getData, { isLoading }] = useFinanceOutMutation();
    const getRequest = async (data: any) => {
        const jwt = localStorage.getItem('jwt');
        await getData({ body: data, jwt })
            .unwrap()
            .then((payload: any) => {
                dispatch(updProfileData(payload.data));
                setgetAlert({ type: 1, msg: payload.message });
                setAmount('');
                setFP('');
            })
            .catch((error: any) => {
                setgetAlert({ type: 2, msg: error.data.message });
            })
    }

    const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // Разрешаем пустую строку
        if (value === '') {
            setAmount('');
            return;
        }

        // Разрешаем только цифры и одну точку, максимум 4 знака после точки
        const regex = /^\d*\.?\d{0,4}$/;

        if (regex.test(value)) {
            setAmount(value);
        }
    };

    const numericAmount = parseFloat(amount) || 0;

    return (
        <>
            <Box sx={{ width: '100%' }}>
                {/* Метод выплаты */}
                {hasNoMethods ? (
                    <Box
                        sx={{
                            backgroundColor: '#fff3e0',
                            border: '1px solid #ffb74d',
                            borderRadius: 1,
                            padding: 1.5,
                            mb: 4,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1
                        }}
                    >
                        <InfoOutlinedIcon sx={{ color: '#f57c00', fontSize: 24, mt: 0.2 }} />
                        <Typography variant="caption" sx={{ fontSize: 16, color: '#e65100', lineHeight: 1.5 }}>
                            Нет доступных методов выплаты. Зайдите в настройки и настройте кошелёк.
                        </Typography>
                    </Box>
                ) : (
                    <FormControl sx={{ width: '100%', mb: 4 }}>
                        <FormLabel htmlFor='wallet'>
                            Метод выплаты
                        </FormLabel>
                        <Select
                            id="wallet"
                            value={selectedWallet}
                            onChange={(e: SelectChangeEvent) => setSelectedWallet(e.target.value)}
                            displayEmpty
                        >
                            {walletMethods.map((m) => (
                                <MenuItem key={m.key} value={m.key} disabled={!m.value}>
                                    {m.label}{m.value ? ` — ${m.value}` : ' (не настроен)'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <FormControl sx={{ width: '100%', mb: 4 }}>
                    <FormLabel htmlFor='amount'>
                        Сумма вывода
                    </FormLabel>
                    <TextField
                        value={amount}
                        inputMode='decimal'
                        size='medium'
                        id="amount"
                        variant="outlined"
                        onChange={handleAmountChange}
                        placeholder="Сумма вывода"
                        inputProps={{
                            inputMode: 'decimal',
                            autoComplete: 'off',
                        }}
                    />
                </FormControl>
                <FormControl sx={{ width: '100%', mb: 4, position: 'relative' }}>
                    <Box display='flex' justifyContent='space-between' alignItems='end' mb={1}>
                        <FormLabel htmlFor='finpas'>
                            Финансовый пароль
                        </FormLabel>
                    </Box>

                    <OutlinedInput
                        value={FP}
                        id="finpas"
                        type={showPassword ? 'text' : 'password'}
                        onInput={(el: ChangeEvent<HTMLInputElement>) => setFP(el.target.value)}
                        inputProps={{
                            autoComplete: 'off',
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff color='error' /> : <Visibility color='error' />}
                                </IconButton>
                            </InputAdornment>
                        }
                        placeholder="Финансовый пароль"
                    />
                </FormControl>

                <FormControl sx={{ width: '100%', mb: 4 }}>
                    <TextField value={comment}
                        onInput={(el: ChangeEvent<HTMLInputElement>) => setComment(el.target.value)}
                        placeholder="Отзыв"
                        label="Отзыв"
                        multiline
                        rows={4}
                        sx={{
                            "&>.MuiInputBase-root": {
                                background: '#ededed',
                                "&>.MuiOutlinedInput-notchedOutline": {
                                    background: "transparent"
                                }
                            }
                        }}
                        inputProps={{
                            autoComplete: 'off',
                        }}
                    />
                </FormControl>
                <Button onClick={SubmitForm} color='primary' variant='contained' sx={{ width: '100%' }} disabled={isLoading || hasNoMethods || user.data.balance < numericAmount}>
                    {isLoading ? "Обработка..." : numericAmount === 0 ? "Вывести" : `Вывести ${numericAmount}$ из ${user?.data?.balance || 0}$`}
                </Button>

            </Box>
            {getAlert && <Alerting get={getAlert} />}
        </>
    )
}